import { PlanBoxBody } from "@/components/atoms/PlanBoxBody";
import { PlanBoxFooterCancelPlan } from "@/components/atoms/PlanBoxFooterCancelPlan";
import { TermBoxPlanCancel } from "@/components/molecules/termbox/TermBoxPlanCancel";
import { TermBoxPlanCancelOptionTerms } from "@/components/atoms/TermBoxPlanCancelOptionTerms";
import {
  CompanyQuery,
  OptionStatus,
  useCancelContractMutation,
} from "@/generated/graphql_billing";
import { useIsSpMode } from "@/hooks";
import { APIContext } from "@/libs/constants";
import { color } from "@/styles/theme";
import { css } from "@emotion/react";
import { Font, Button } from "@moneyforward/mfx_biz_react_components";
import { useRouter } from "next/router";
import { useCallback, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { PageTitle, FormCheckBox, FormErrorMessage } from "../molecules";
import Spacer from "@/components/atoms/utilities/Spacer";
import { PlanCancellationStepper } from "@/components/atoms/PlanCancellationStepper";
import useRollbarUtil from "../hooks/useRollbarUtil";
import paths from "@/libs/paths";
import { PlanBox } from "@/components/molecules/PlanBox";
import { PlanBoxPlanDetail } from "@/components/atoms/PlanBoxPlanDetail";

type Props = {
  now: Date;
  company: CompanyQuery["company"];
  onClickCancel?: () => void;
  setIsCanecelPage?: any;
};

export const PlanCancel = ({ now, company, onClickCancel }: Props) => {
  const router = useRouter();

  const { getNotifyErrorHandlerForGraphQL } = useRollbarUtil();
  const isSpMode = useIsSpMode();

  // data
  const contractedPlan = company.planContract.plan;
  const contractedOptions = company.optionContracts
    ? company.optionContracts
        .map((contract) => contract.option)
        .filter((option) => option.status === OptionStatus.Active)
    : [];

  // form
  const [checked, setChecked] = useState<boolean>(false);
  const methods = useForm({
    defaultValues: {
      termChecked: false,
    },
  });

  const [cancelContractMutation] = useCancelContractMutation({
    onError: getNotifyErrorHandlerForGraphQL("Failed to cancel contract"),
    context: {
      api: APIContext.BILLING,
    },
  });

  const handleSubmit = useCallback(async () => {
    const { data } = await cancelContractMutation();
    if (data.cancelContract.result) {
      await router.push(paths.PlanCancelCompleted);
    }
  }, [cancelContractMutation]);

  return (
    <div css={container(isSpMode)}>
      <PageTitle
        mainTitle={"解約手続き"}
        subTitle={
          <Font variant="l">
            プランとオプションを解約します。
            <br />
            ご利用中のサービスは来月からご利用いただけません。
          </Font>
        }
      >
        <Spacer size={16} />
        <PlanCancellationStepper step={2} />
      </PageTitle>

      {/* Plan and Option Details */}
      <Font variant="lb" css={sectionTitle}>
        解約内容
      </Font>

      <PlanBox
        now={now}
        plan={contractedPlan}
        optionContracts={contractedOptions}
      >
        <PlanBoxBody>
          <PlanBoxPlanDetail isPlanPriceTaxExcluded={false} />
        </PlanBoxBody>
        <PlanBoxFooterCancelPlan />
      </PlanBox>

      <Font variant="lb" css={[sectionTitle, { color: color.attention }]}>
        解約される前に必ずご確認ください
      </Font>

      {/* Plan Cancellation Terms */}
      <TermBoxPlanCancel />

      {/* Option Cancellation Terms */}
      {contractedOptions.length > 0 && (
        <TermBoxPlanCancelOptionTerms contractedOptions={contractedOptions} />
      )}

      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(handleSubmit)}
          css={{ display: "flex", flexDirection: "column", gap: "32px" }}
        >
          {/* Acknowledgement Checkbox */}
          <div css={checkboxContainer}>
            <FormCheckBox
              name="termChecked"
              checked={checked}
              label="上記の注意事項に同意する"
              onChange={() => setChecked(!checked)}
              registerOptions={{
                required: "同意が必要です",
              }}
            />
            <FormErrorMessage name="termChecked" />
          </div>

          {/* Buttons */}
          <div css={buttonContainer}>
            <Button
              variant="secondary"
              type="button"
              onClick={() => {
                onClickCancel();
              }}
            >
              解約をキャンセル
            </Button>
            <Button variant="destructive" type="submit">
              解約
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

const container = (isSpMode: boolean) =>
  css({
    width: isSpMode ? "auto" : "640px",
    margin: "0px auto 0 auto",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  });

const sectionTitle = css({
  padding: "8px 0px 0px 0px",
});

const checkboxContainer = css({
  textAlign: "center",
});

const buttonContainer = css({
  display: "flex",
  gap: "16px",
  justifyContent: "center",
});
